'use client'

import { Box, Stack, Typography } from '@mui/material'
import { useTranslations } from 'use-intl'
import FareCategorySidebar from './FareCategorySidebar'
import FarePlanCard from './FarePlanCard'
import { useMaxRowHeights } from '@/src/hooks/srp/fareFamilySRP/useMaxRowHeights'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FarePlanState } from '../types'

type Props = {
    farePlanState: FarePlanState
    setFarePlanState: React.Dispatch<React.SetStateAction<FarePlanState>>
    plansScrollRef: React.RefObject<HTMLDivElement>
}

export default function FareSelectionContent({
    farePlanState,
    setFarePlanState,
    plansScrollRef
}: Props) {
    const { filteredPlans, selectedPlanFareKey, selectedFilters } = farePlanState
    const tFareFamily = useTranslations('fareFamily')

    const isExclusiveBenefitsAvailable = filteredPlans.some(planObj => planObj.plan.benefitsText['exclusiveBenefits'])

    const benefitKeys = useMemo(() => {
        const base = Object.keys(filteredPlans?.[0]?.plan?.benefitsText || {})
        return isExclusiveBenefitsAvailable ? [...base, 'exclusiveBenefits'] : base
    }, [filteredPlans, isExclusiveBenefitsAvailable])

    const { rowHeights, registerRef } = useMaxRowHeights(benefitKeys.length)

    const cardRefs = useRef<(HTMLDivElement | null)[]>([])
    const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null)

    useEffect(() => {
        const timer = requestAnimationFrame(() => {
            const heights = cardRefs.current.map(el => el?.offsetHeight || 0)
            const maxHeight = Math.max(...heights)
            setMaxCardHeight(prev => (prev !== maxHeight ? maxHeight : prev))
        })
        return () => cancelAnimationFrame(timer)
    }, [filteredPlans])

    // Show 'NoPlansAvailable' ONLY when filters are applied and filtered is empty
    const showNoPlansAvailable =
        filteredPlans.length === 0 &&
        selectedFilters.length > 0

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
            <FareCategorySidebar
                isExclusiveBenefitsAvailable={isExclusiveBenefitsAvailable}
                rowHeights={rowHeights}
                height={maxCardHeight}
                benefitKeys={benefitKeys}
                registerRef={registerRef}
            />

            <Box
                sx={{
                    overflowX: 'auto',
                    display: 'grid',
                    width: '100%',
                    height: '100%',
                    pt: 2.3,
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    justifyContent: showNoPlansAvailable ? 'center' : 'flex-start',
                    alignItems: 'center'
                }}
                ref={plansScrollRef}
            >
                {showNoPlansAvailable ? (
                    <Typography variant="subtitle1" color="text.secondary">
                        {tFareFamily('NoPlansAvailable')}
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
                        {filteredPlans.map(({ plan }, colIndex) => (
                            <FarePlanCard
                                key={plan.fareKey}
                                plan={plan}
                                isSelected={plan.fareKey === selectedPlanFareKey}
                                onSelect={() =>
                                    setFarePlanState(prev => ({
                                        ...prev,
                                        selectedPlanFareKey: plan.fareKey
                                    }))
                                }
                                cardRef={el => {
                                    cardRefs.current[colIndex] = el
                                }}
                                forcedHeight={maxCardHeight}
                                benefitKeys={benefitKeys}
                                rowHeights={rowHeights}
                                registerRef={registerRef}
                                colIndex={colIndex}
                                isExclusiveBenefitsAvailable={isExclusiveBenefitsAvailable}
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    )
}